import { resolveAppMode } from "@/utils/appMode";
import { CLIENT_ROLES } from "@/constants/roles";
import { redirectToSubdomainWithSession } from "@/utils/sessionHandoff";

// Which panel a role belongs to.
function panelForRole(role) {
  return CLIENT_ROLES.includes(role) ? "client" : "admin";
}

// Subdomain that hosts each panel, in subdomain mode.
const PANEL_SUBDOMAIN = { admin: "admin", client: "app" };
// Path that hosts each panel, in path mode (Firebase / current prod).
const PANEL_PATH = { admin: "/admin", client: "/app" };

/**
 * Where should a just- (or already-) authenticated user land, given their role.
 *
 * Path mode (Firebase / current prod): behaviour is unchanged — returns the
 * same "/admin" or "/app" path (or the `from` deep-link) that the app used
 * before, to be passed to navigate().
 *
 * Subdomain mode:
 *   - If the user's panel lives on the current subdomain, returns "/" (the
 *     panel is mounted at the root there).
 *   - If it lives on a sibling subdomain, performs a full-page redirect to it
 *     carrying the session, and returns null — the caller must NOT navigate,
 *     the page is being replaced.
 *
 * @param {string} role
 * @param {{ from?: string }} [opts] optional deep-link path (path mode only)
 * @returns {string|null} a path for navigate(), or null if a cross-subdomain
 *   redirect was triggered instead
 */
export function resolvePanelDestination(role, opts = {}) {
  const panel = panelForRole(role);
  const mode = resolveAppMode().mode;

  // Path mode — unchanged behaviour, including honouring the `from` deep link.
  if (mode !== "admin" && mode !== "client") {
    return opts.from || PANEL_PATH[panel];
  }

  // Subdomain mode.
  const currentPanel = mode === "admin" ? "admin" : "client";
  if (currentPanel === panel) {
    // Already on the right subdomain — the panel is mounted at "/".
    return "/";
  }
  // Wrong subdomain — hop to the sibling, carrying the session.
  redirectToSubdomainWithSession(PANEL_SUBDOMAIN[panel]);
  return null;
}
